import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const today = new Date().toISOString().slice(0, 10);

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={
    status === "Present" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status?.startsWith("Absent") ? "bg-red-50 text-red-700 border-red-200"
    : status?.startsWith("Present") ? "bg-amber-50 text-amber-700 border-amber-200"
    : status === "On Leave" ? "bg-blue-50 text-[#002FA7] border-blue-200"
    : "bg-slate-50 text-slate-500 border-slate-200"}>
    {status}
  </Badge>
);

export default function StaffAttendance() {
  const [mode, setMode] = useState("daily");
  const [dateStr, setDateStr] = useState(today);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [deptId, setDeptId] = useState("all");
  const [teacherId, setTeacherId] = useState("all");
  const [editRec, setEditRec] = useState(null);
  const [deleteRecId, setDeleteRecId] = useState(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const readOnly = user?.role !== "admin"; // HOD: view & download only

  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/admin/departments")).data });
  const { data: teachers = [] } = useQuery({ queryKey: ["teachers"], queryFn: async () => (await api.get("/admin/teachers")).data });

  const params = () => {
    const p = { mode, date_str: dateStr, month, year };
    if (deptId !== "all") p.department_id = deptId;
    if (teacherId !== "all") p.teacher_id = teacherId;
    return p;
  };

  const { data: rows = [] } = useQuery({
    queryKey: ["staff-report", mode, dateStr, month, year, deptId, teacherId],
    queryFn: async () => (await api.get("/staff/admin/report", { params: params() })).data,
  });

  const qs = () => `?${new URLSearchParams(params()).toString()}`;
  const filteredTeachers = teachers.filter((t) => deptId === "all" || t.department_id === deptId);

  const saveRec = async () => {
    try {
      const payload = {};
      const ci = editRec.clock_in === "—" ? "" : editRec.clock_in;
      const co = editRec.clock_out === "—" ? "" : editRec.clock_out;
      if (ci) payload.clock_in = ci;
      if (co) payload.clock_out = co;
      await api.put(`/staff/admin/record/${editRec.record_id}`, payload);
      toast.success("Record updated — late/early flags recalculated");
      setEditRec(null);
      queryClient.invalidateQueries({ queryKey: ["staff-report"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delRec = async () => {
    try {
      await api.delete(`/staff/admin/record/${deleteRecId}`);
      toast.success("Record deleted");
      setDeleteRecId(null);
      queryClient.invalidateQueries({ queryKey: ["staff-report"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Layout title="Staff Attendance Report">
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="staff-report-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Report period</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="report-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(mode === "daily" || mode === "weekly") && (
            <div className="space-y-1.5">
              <Label>{mode === "daily" ? "Date" : "Any date in the week"}</Label>
              <Input type="date" data-testid="report-date-input" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
            </div>
          )}
          {mode === "monthly" && (
            <div className="space-y-1.5">
              <Label>Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid="report-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(mode === "monthly" || mode === "yearly") && (
            <div className="space-y-1.5">
              <Label>Year</Label>
              <Input type="number" data-testid="report-year-input" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Select value={deptId} onValueChange={(v) => { setDeptId(v); setTeacherId("all"); }}>
              <SelectTrigger data-testid="report-dept-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Teacher</Label>
            <Select value={teacherId} onValueChange={setTeacherId}>
              <SelectTrigger data-testid="report-teacher-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All teachers</SelectItem>
                {filteredTeachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" data-testid="download-excel-btn"
              onClick={() => downloadFile(`/staff/admin/report.xlsx${qs()}`, "staff_attendance.xlsx")}>
              <Download className="h-4 w-4" /> Excel
            </Button>
            <Button variant="outline" data-testid="download-staff-pdf-btn"
              onClick={() => downloadFile(`/staff/admin/report.pdf${qs()}`, "staff_attendance.pdf")}>
              <Download className="h-4 w-4" /> PDF
            </Button>
            <Button variant="outline" data-testid="download-staff-summary-excel-btn"
              onClick={() => downloadFile(`/staff/admin/report-summary.xlsx${qs()}`, "staff_attendance_summary.xlsx")}>
              <Download className="h-4 w-4" /> Summary Excel
            </Button>
            <Button variant="outline" data-testid="download-staff-summary-pdf-btn"
              onClick={() => downloadFile(`/staff/admin/report-summary.pdf${qs()}`, "staff_attendance_summary.pdf")}>
              <Download className="h-4 w-4" /> Summary PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="staff-report-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Sl. No.</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Clock-in time</TableHead>
              <TableHead>Clock-out time</TableHead>
              <TableHead>Late</TableHead>
              <TableHead>Early departure</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10" data-testid="staff-report-empty">
                No data for the selected period.
              </TableCell></TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={`${r.date}-${r.name}-${i}`} data-testid={`staff-report-row-${i}`}>
                <TableCell className="font-data">{r.date}</TableCell>
                <TableCell className="text-right font-data">{i + 1}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell className="font-data">{r.clock_in}</TableCell>
                <TableCell className="font-data">{r.clock_out}</TableCell>
                <TableCell>{r.late === "Yes" ? <span className="text-amber-700 font-medium">Yes</span> : r.late}</TableCell>
                <TableCell>{r.early_departure === "Yes" ? <span className="text-amber-700 font-medium">Yes</span> : r.early_departure}</TableCell>
                <TableCell>
                  {r.record_id && !readOnly && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" data-testid={`edit-staff-record-${i}`} onClick={() => setEditRec(r)}
                        className="text-slate-400 hover:text-slate-950 hover:bg-slate-100"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" data-testid={`delete-staff-record-${i}`} onClick={() => setDeleteRecId(r.record_id)}
                        className="text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editRec} onOpenChange={() => setEditRec(null)}>
        <DialogContent className="bg-white rounded-md" data-testid="edit-staff-record-dialog">
          <DialogHeader><DialogTitle>Edit — {editRec?.name} · {editRec?.date}</DialogTitle></DialogHeader>
          {editRec && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Clock-in time</Label>
                  <Input type="time" data-testid="edit-clock-in-input"
                    value={editRec.clock_in === "—" ? "" : editRec.clock_in || ""}
                    onChange={(e) => setEditRec({ ...editRec, clock_in: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Clock-out time</Label>
                  <Input type="time" data-testid="edit-clock-out-input"
                    value={editRec.clock_out === "—" ? "" : editRec.clock_out || ""}
                    onChange={(e) => setEditRec({ ...editRec, clock_out: e.target.value })} /></div>
              </div>
              <p className="text-xs text-slate-400">Late / early-departure flags are recalculated from the department settings.</p>
              <Button onClick={saveRec} data-testid="save-staff-record-btn" className="w-full bg-slate-950 hover:bg-slate-800 text-white">
                Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteRecId} onOpenChange={() => setDeleteRecId(null)}>
        <AlertDialogContent className="bg-white rounded-md" data-testid="delete-staff-record-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this staff attendance record?</AlertDialogTitle>
            <AlertDialogDescription>The day will show as Absent (or Weekly Off / Holiday) for that teacher.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-staff-record-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-staff-record-confirm" onClick={delRec}
              className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
